import React, { useEffect, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import './styles.scss';
import {
  ACCOUNTS_HEADERS,
  ACCOUNTS_TYPES_MAP,
  BRANDS_HEADERS,
  BRANDS_TYPES_MAP,
  ITEMS_HEADERS,
  ITEMS_TYPES_MAP,
  STORES_HEADERS,
  STORES_TYPES_MAP
} from './headers';
import {
  ACCOUNTS_DETAIL_SERIES,
  STORES_DETAIL_SERIES,
  ITEMS_DETAIL_SERIES,
  BRAND_RESUME_PORTFOLIO
} from 'apollo/queries';
import {
  ACCOUNTS_TYPE,
  STORES_TYPE,
  ITEMS_TYPE,
  DEFAULT_PER_PAGE,
  HeaderType,
  BRANDS_TYPE
} from 'utils/types';
import { getData, applyStyle, formatTo, titleCase } from 'utils/functions';
import Paginator from 'components/Paginator';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import TableHeaders from 'components/TableHeaders';
import isEqual from 'lodash/isEqual';

interface Props {
  clientID: string;
  globalFilter: any;
  type: string;
  search: string;
  selectedTab: string;
  onRowClick?(key: string, row: any): void;
  onQueryFinish(total: string): void;
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * @deprecated Use spins/react-table instead
 */
const FullTable: React.FC<Props> = ({
  onRowClick,
  type,
  search,
  selectedTab,
  clientID,
  globalFilter,
  onQueryFinish
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSortDirection, setCurrentSortDirection] = useState('DESC');
  const prevGlobalFilter = usePrevious(globalFilter);
  let rowHeaders: Array<HeaderType>, rowTypes: Map<string, string>, query: any;

  switch (type) {
    case ACCOUNTS_TYPE:
      rowHeaders = ACCOUNTS_HEADERS;
      rowTypes = ACCOUNTS_TYPES_MAP;
      query = ACCOUNTS_DETAIL_SERIES;
      break;
    case STORES_TYPE:
      rowHeaders =
        (window.location.href.indexOf('details') > -1 &&
          window.location.href.indexOf('group-details') === -1 &&
          globalFilter.items &&
          globalFilter.items.length > 0) ||
        (globalFilter.items && globalFilter.items.length > 0)
          ? STORES_HEADERS.slice(0, -1)
          : STORES_HEADERS;
      rowTypes = STORES_TYPES_MAP;
      query = STORES_DETAIL_SERIES;
      break;
    case ITEMS_TYPE:
      rowHeaders = ITEMS_HEADERS;
      rowTypes = ITEMS_TYPES_MAP;
      query = ITEMS_DETAIL_SERIES;
      break;
    case BRANDS_TYPE:
      rowHeaders = BRANDS_HEADERS;
      rowTypes = BRANDS_TYPES_MAP;
      query = BRAND_RESUME_PORTFOLIO;
      break;
    default:
      rowHeaders = ACCOUNTS_HEADERS;
      rowTypes = ACCOUNTS_TYPES_MAP;
      query = ACCOUNTS_DETAIL_SERIES;
      break;
  }
  const defaultHeader: HeaderType = rowHeaders.find(header => header.default);
  const [currentSortedBy, setCurrentSortedBy] = useState(
    defaultHeader ? defaultHeader.sortBy : null
  );
  useEffect(() => {
    if (type !== selectedTab || !isEqual(prevGlobalFilter, globalFilter)) {
      setCurrentSortDirection('DESC');
      setCurrentSortedBy(defaultHeader ? defaultHeader.sortBy : null);
      setCurrentPage(1);
    }
  }, [defaultHeader, selectedTab, type, prevGlobalFilter, globalFilter]);

  const showRow = (row: { [x: string]: any }) => {
    let rowNode = [];
    rowTypes.forEach((val: any, key: React.ReactText) => {
      let cell = null;

      if (key === 'name') {
        cell = (
          <td key={`${val}-${key}`} style={applyStyle(key, row[key])}>
            {formatTo(val, titleCase(row[key]))}
          </td>
        );
      } else if (
        (window.location.href.indexOf('details') > -1 &&
          window.location.href.indexOf('group-details') === -1 &&
          key === 'topOpportunity' &&
          globalFilter.items &&
          globalFilter.items.length > 0) ||
        (globalFilter.items && globalFilter.items.length > 0 && key === 'topOpportunity')
      ) {
        cell = null;
      } else {
        cell = (
          <td key={`${val}-${key}`} style={applyStyle(key, row[key])}>
            {formatTo(val, row[key])}
          </td>
        );
      }

      rowNode.push(cell);
    });
    let key;
    switch (type) {
      case STORES_TYPE:
        key = row.storeTag;
        break;
      case ITEMS_TYPE:
        key = row.upc;
        break;
      default:
        key = row.name;
        break;
    }
    return (
      <tr
        className={onRowClick ? 'clickable' : null}
        onClick={() => onRowClick && onRowClick(key, row)}
        key={Math.random()}
      >
        {rowNode}
      </tr>
    );
  };

  const getPageable = (currentPage, sortedBy, sortDirection) => {
    return {
      page: currentPage,
      pageSize: DEFAULT_PER_PAGE,
      sortedBy,
      sortDirection,
      search: search,
      searchBy: ['name']
    };
  };

  const handleHeaderClick = (refetch, sortBy: string, sortDirection: string) => {
    setCurrentSortedBy(sortBy);
    setCurrentSortDirection(sortDirection);
    refetch({
      pageable: getPageable(currentPage, currentSortedBy, currentSortDirection)
    });
  };

  return (
    <QueryRetryWrapper
      query={query}
      variables={{
        clientID: clientID,
        globalFilter: globalFilter,
        pageable: getPageable(currentPage, currentSortedBy, currentSortDirection)
      }}
      fetchPolicy="cache-first"
      onCompleted={data => {
        if (getData(data)) {
          onQueryFinish(getData(data).page.totalItems.toString());
        }
      }}
      onError={() => {
        onQueryFinish('?');
      }}
    >
      {({ data, refetch }) => {
        return (
          <React.Fragment>
            <Table className={`full-table ${type.toLowerCase().replace(' ', '-')}`} responsive>
              <colgroup>
                {rowHeaders.map((header, index) => (
                  <col key={index} />
                ))}
              </colgroup>
              <TableHeaders
                currentSortedBy={currentSortedBy}
                currentSortDirection={currentSortDirection}
                headers={rowHeaders}
                onSort={(sortBy, sortDirection) =>
                  handleHeaderClick(refetch, sortBy, sortDirection)
                }
              />
              <tbody>{getData(data).data.map(showRow)}</tbody>
            </Table>
            <Paginator
              currentPage={currentPage}
              totalPages={getData(data).page.totalPages}
              onNextClick={nextPage => {
                setCurrentPage(nextPage);
                refetch({
                  pageable: getPageable(currentPage, currentSortedBy, currentSortDirection)
                });
              }}
              onPreviousClick={previousPage => {
                setCurrentPage(previousPage);
                refetch({
                  pageable: getPageable(currentPage, currentSortedBy, currentSortDirection)
                });
              }}
            />
          </React.Fragment>
        );
      }}
    </QueryRetryWrapper>
  );
};

export default FullTable;
